"use client";
import type {ReactNode} from "react";
import {getClassColorAccentToken} from '../../../lib/classColors'
import {PassAccess} from "@fitro/customer-ui/PassAccess";
import {AcceptedPassCard} from "@fitro/customer-ui/AcceptedPassCard";
import {usePassAccessLabels} from "../pass/usePassAccessLabels";
import { useTranslations } from "next-intl";
import {
  BenefitProviderLogo,
  parseBenefitProvider,
} from "@fitro/customer-ui/BenefitProvider";
import {
  ClientSurfaceCard,
  ClientSurfaceSummaryGrid,
  ClientSurfaceSummaryItem,
} from "../shared/ClientSurfaceCard";
import { PricingConditions } from "./PublicPricingTable";
import { BenefitCardRequirement } from "@fitro/customer-ui/BenefitProvider";
import {CreditCard} from "lucide-react";
import { LowestPrice30dNote } from "../promotions/PriceNotes";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  getClassColorToken,
  normalizeClassColorKey,
  CLASS_COLOR_NEUTRAL,
} from "../../../lib/classColors";
import { formatPriceMinor } from "../../../lib/formatPriceMinor";
import type { PublicPassOffer } from "../../../services/public-passes.service";
import { formatPassType, formatValidity } from "./publicPricingModel";

export function PublicPassOfferCard({
  offer,
  variant = "catalog",
  indicator,
}: {
  offer: PublicPassOffer;
  variant?: "catalog";
  indicator?: ReactNode;
} | {
  // An accepted pass says which pass admits to a class, not what it costs: it is not an offer, so it has no price.
  offer: Pick<PublicPassOffer, "id" | "name" | "entriesTotal"> & Partial<PublicPassOffer>;
  variant: "accepted";
  indicator?: ReactNode;
}) {
  const accessLabels = usePassAccessLabels();
  const compact = variant === "accepted";
  const hasAccess = !compact && offer.accessSummary && (offer.accessSummary.groupMode === 'all' || offer.accessSummary.groupClasses.length > 0 || offer.accessSummary.individualClasses.length > 0);
  const { locale, t } = useTranslation("landing");
  const m = useTranslations("landing.publicEntry.pricing.compact");
  const money = (amount: number) =>
    formatPriceMinor(amount, offer.currency ?? "PLN", locale);
  const promo = offer.automaticPromotion;
  const first = offer.newCustomerPromotion;
  const provider = offer.isBenefit
    ? parseBenefitProvider(offer.benefitProvider)
    : null;
  const entries = offer.type === "one_time"
    ? 1
    : typeof offer.entriesTotal === "number"
      ? offer.entriesTotal
      : offer.isBenefit
        ? formatPassType(offer, t)
        : m("open");
  const priceNotes = (
    <>
      {typeof promo?.lowestPrice30dMinor === "number" ? (
        <div className="space-y-2 text-center">
          <LowestPrice30dNote
            price={money(promo.lowestPrice30dMinor)}
            className="text-sm leading-5 text-slate-600"
          />
        </div>
      ) : null}
      {first ? (
        <div className="space-y-2 text-center">
          <p
            className="text-base font-semibold leading-6 text-emerald-900"
          >
            {t("publicEntry.pricing.newCustomerPromotion", {
              price: money(first.finalAmountMinor),
            })}
          </p>
          {typeof first.lowestPrice30dMinor === "number" &&
          first.lowestPrice30dMinor !== promo?.lowestPrice30dMinor ? (
            <LowestPrice30dNote
              price={money(first.lowestPrice30dMinor)}
              className="text-sm leading-5 text-slate-600"
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
  const compactMeta = offer.entriesTotal === undefined && !offer.type && !offer.isBenefit ? null : (
    <>
      {offer.isBenefit
        ? formatPassType(offer, t)
        : typeof entries === "number"
          ? m("entryCount", { count: entries })
          : m("open")}
      {typeof offer.validityDays === "number" ? (
        <>
          {" · "}
          {t("publicEntry.pricing.validity.days", {
            days: String(offer.validityDays),
          })}
        </>
      ) : null}
    </>
  );
  const freeBenefit = Boolean(offer.isBenefit) && (offer.priceMinor ?? 0) <= 0 && !promo;
  const backgroundColor = getClassColorToken(
    normalizeClassColorKey(offer.colorKey) ?? CLASS_COLOR_NEUTRAL,
  );
  if (compact) {
    return (
      <AcceptedPassCard
        name={offer.name}
        meta={compactMeta}
        logo={provider ? <BenefitProviderLogo provider={provider} className="h-6" /> : null}
        indicator={indicator}
        backgroundColor={backgroundColor}
      />
    );
  }
  return (
    <ClientSurfaceCard
      className="@container h-full"
      style={{ backgroundColor }}
      contentClassName={hasAccess ? "flex h-full flex-col p-4 pb-1" : "flex h-full flex-col p-4"}
    >
      <div className="min-w-0">
        <h3 className="text-lg font-semibold leading-6 text-slate-900 [overflow-wrap:anywhere]">
          {offer.name}
        </h3>
        {indicator}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* A free benefit pass has no price to show, not even a placeholder. */}
          {freeBenefit ? null : (
            <span className="inline-flex items-baseline gap-1.5">
              {typeof promo?.lowestPrice30dMinor === "number" ? (
                <s className="whitespace-nowrap text-xs text-slate-600">
                  {money(promo.lowestPrice30dMinor)}
                </s>
              ) : null}
              <strong className="whitespace-nowrap text-2xl font-semibold leading-7 tabular-nums text-slate-900">
                {money(promo?.finalAmountMinor ?? offer.priceMinor ?? 0)}
              </strong>
            </span>
          )}
          {offer.isBenefit ? (
            <BenefitCardRequirement icon={<CreditCard aria-hidden="true" className="h-4 w-4 shrink-0" />}
              provider={provider}
              label={t("publicEntry.pricing.cardRequired")}
              genericLabel={t("publicEntry.pricing.benefitCardRequiredShort")}
            />
          ) : null}
        </div>
      </div>
      <div>
        <ClientSurfaceSummaryGrid className="mt-4 grid-cols-2 gap-x-3 border-t border-slate-900/10 pt-3">
          <ClientSurfaceSummaryItem
            size="large"
            label={m("entriesLabel")}
            value={entries}
          />
          <ClientSurfaceSummaryItem
            size="large"
            label={m("validityColumn")}
            value={formatValidity(offer, t)}
          />
        </ClientSurfaceSummaryGrid>
        {typeof promo?.lowestPrice30dMinor === "number" || first ? (
          <PricingConditions fullWidth>{priceNotes}</PricingConditions>
        ) : null}
      </div>
      {hasAccess ? <div className="mt-auto pt-3"><div className="border-t border-slate-900/10"><PassAccess colorToken={getClassColorAccentToken} summary={offer.accessSummary} labels={accessLabels} size="regular" /></div></div> : null}
    </ClientSurfaceCard>
  );
}
